import { ApiError } from '@nexo/api';
import type { DeleteAccountBlocked } from '@nexo/types';
import { Button, Field, Input, Modal, useToast } from '@nexo/ui';
import { useMutation } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';
import { useAuth } from '../auth/AuthProvider';

/** GDPR rights: download a copy of your data and delete your account. */
export function PrivacySection() {
  const { t } = useTranslation();
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  const download = useMutation({
    mutationFn: api.account.exportData,
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nexo-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) =>
      toast({ title: t('No se pudo descargar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <section className="mt-4 rounded-2xl border border-border bg-surface p-6 shadow-panel">
      <h2 className="text-sm font-semibold">{t('Tus datos y privacidad')}</h2>
      <p className="mt-1 text-sm text-muted">
        {t('Descarga una copia de todo lo que Nexo guarda sobre ti o elimina tu cuenta.')}{' '}
        <Link to="/privacy" className="text-accent hover:underline">
          {t('Política de privacidad')}
        </Link>
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          variant="secondary"
          icon={<Download className="size-4" />}
          loading={download.isPending}
          onClick={() => download.mutate()}
        >
          {t('Descargar mis datos')}
        </Button>
        <Button
          variant="ghost"
          icon={<Trash2 className="size-4" />}
          className="text-danger"
          onClick={() => setDeleting(true)}
        >
          {t('Eliminar mi cuenta')}
        </Button>
      </div>
      {deleting && <DeleteAccountDialog onClose={() => setDeleting(false)} />}
    </section>
  );
}

function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [password, setPassword] = useState('');
  const [blocked, setBlocked] = useState<DeleteAccountBlocked['teams'] | null>(null);
  const [error, setError] = useState<string | undefined>();

  const remove = useMutation({
    mutationFn: () => api.account.delete(password),
    onSuccess: () => void logout(),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setBlocked((err.body as DeleteAccountBlocked).teams);
      } else setError(errorMessage(err));
    },
  });

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password) return setError(t('Obligatorio'));
    remove.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={t('Eliminar tu cuenta')}
      description={t(
        'Se borran tu perfil, tus sesiones, notificaciones y ajustes. Tus mensajes y tareas se quedan en sus equipos como «Cuenta eliminada». No se puede deshacer.',
      )}
    >
      {blocked ? (
        <div className="flex flex-col gap-3 text-sm">
          <p>
            {t(
              'Eres propietario de estos equipos con más personas. Transfiere la propiedad o elimínalos antes de borrar tu cuenta:',
            )}
          </p>
          <ul className="list-disc pl-5">
            {blocked.map((team) => (
              <li key={team.id}>
                {team.name}{' '}
                <span className="text-muted">
                  · {t('{{count}} miembros', { count: team.memberCount })}
                </span>
              </li>
            ))}
          </ul>
          <Button variant="secondary" className="self-end" onClick={onClose}>
            {t('Entendido')}
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field label={t('Escribe tu contraseña para confirmar')} error={error}>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(undefined);
                }}
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('Cancelar')}
            </Button>
            <Button type="submit" variant="danger" loading={remove.isPending}>
              {t('Eliminar definitivamente')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
